<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-6xl"
    @close="close"
  >
    <template #header>
      <h2 class="text-xl font-bold text-textPrimary text-left">Detalhes da avaliação</h2>
    </template>

    <div v-if="row" class="flex flex-col lg:flex-row gap-6 text-left">
      <div class="flex-1 min-w-0">
        <CheckEvidencePanel
          :check-id="row.checkId"
          test-id-prefix="check-validation-details"
          @loaded="onEvidenceLoaded"
        />
      </div>

      <div class="w-full lg:w-[380px] shrink-0 flex flex-col gap-4">
        <CheckSummaryCard
          :row="row"
          :typed-hours="context ? { checkin: context.checkin, checkout: context.checkout } : null"
          :timezone="context?.timezone ?? ''"
          :doctor-name-override="context?.doctorName ?? null"
          :crm="context?.crm ?? null"
        />

        <div class="flex flex-col gap-2">
          <p class="text-sm font-bold text-textPrimary">Histórico</p>

          <div v-if="isHistoryLoading" class="flex flex-col gap-3 animate-pulse">
            <div v-for="n in 3" :key="n" class="flex gap-3">
              <div class="w-3 h-3 rounded-full bg-slate-200 shrink-0 mt-1" />
              <div class="flex flex-col gap-1.5 flex-1">
                <div class="h-3.5 w-2/3 bg-slate-200 rounded" />
                <div class="h-3 w-1/3 bg-slate-200 rounded" />
              </div>
            </div>
          </div>

          <p v-else-if="historyError" class="text-sm text-negative">
            {{ historyError }}
          </p>

          <p v-else-if="sortedEvents.length === 0" class="text-sm text-neutral50">
            Nenhum evento registrado.
          </p>

          <ol v-else class="flex flex-col">
            <li v-for="(event, index) in sortedEvents" :key="event.id" class="flex gap-3">
              <div class="flex flex-col items-center">
                <div
                  class="w-3 h-3 rounded-full shrink-0 mt-1"
                  :class="index === 0 ? 'bg-primary30' : 'bg-[#16161d]'"
                />
                <div v-if="index < sortedEvents.length - 1" class="w-px flex-1 bg-[#d8d8e2] my-1" />
              </div>

              <div class="flex flex-col gap-0.5 pb-4">
                <p class="text-sm text-textPrimary">
                  <span class="font-bold">{{ eventLabel(event) }}</span>
                  <span v-if="transitionLabel(event)" class="text-neutral50">
                    · {{ transitionLabel(event) }}
                  </span>
                </p>
                <p class="text-xs text-neutral50">
                  {{ formatDateTimeBR(event.createdAt) }}, por
                  {{
                    event.performedBy.name
                      ? getFirstAndSecondName(event.performedBy.name)
                      : 'Sistema'
                  }}
                </p>
                <p v-if="event.rejectionReason" class="text-sm text-textPrimary">
                  Motivo: {{ reasonLabel(event.rejectionReason) }}
                </p>
                <p v-if="event.observation" class="text-sm text-textPrimary italic">
                  "{{ event.observation }}"
                </p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <OutlineButton
          text="Fechar"
          width="160px"
          height="48px"
          data-testid="check-validation-details-close-button"
          @click="close"
        />
        <OutlineButton
          v-if="canReverse"
          text="Reverter aprovação"
          width="auto"
          height="48px"
          class="!text-negative !border-negative hover:!bg-red-50"
          data-testid="check-validation-details-reverse-button"
          @click="emit('reverse', row!)"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  CheckValidationStatus,
  type CheckRejectionReason,
  type CheckValidationHistoryEvent
} from '@/Network/Types/Responses/CheckValidationResponses'
import {
  CHECK_REJECTION_REASON_LABELS,
  CHECK_VALIDATION_EVENT_TYPE_LABELS,
  CHECK_VALIDATION_STATUS_LABELS
} from '@/constants/check-validation.constants'
import { formatDateTimeBR } from '@/utils/date'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'

import CheckEvidencePanel from './CheckEvidencePanel.vue'
import type { CheckEvidenceContext } from './CheckEvidencePanel.vue'
import CheckSummaryCard from './CheckSummaryCard.vue'
import type { CheckValidationRow } from '../types'

const props = defineProps<{
  modelValue: boolean
  row: CheckValidationRow | null
  history: CheckValidationHistoryEvent[]
  isHistoryLoading: boolean
  historyError: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  reverse: [row: CheckValidationRow]
}>()

const context = ref<CheckEvidenceContext | null>(null)

const canReverse = computed(() => props.row?.status === CheckValidationStatus.APPROVED)

const sortedEvents = computed(() =>
  [...props.history].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
)

const eventLabel = (event: CheckValidationHistoryEvent): string =>
  CHECK_VALIDATION_EVENT_TYPE_LABELS[event.eventType] ?? event.eventType

const transitionLabel = (event: CheckValidationHistoryEvent): string | null => {
  if (!event.previousStatus || event.previousStatus === event.newStatus) return null
  const from = CHECK_VALIDATION_STATUS_LABELS[event.previousStatus] ?? event.previousStatus
  const to = CHECK_VALIDATION_STATUS_LABELS[event.newStatus] ?? event.newStatus
  return `${from} → ${to}`
}

const reasonLabel = (reason: CheckRejectionReason): string =>
  CHECK_REJECTION_REASON_LABELS[reason] ?? reason

function onEvidenceLoaded(loaded: CheckEvidenceContext) {
  context.value = loaded
}

watch(
  () => [props.row?.checkId, props.modelValue],
  () => {
    context.value = null
  }
)

function close() {
  emit('update:modelValue', false)
}
</script>
